<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-center">
                          <h4 class="nk-block-title mb-0">{{ $t('dm_kalk_title') }}</h4>
                          <span v-if="preisliste" class="text-muted small">Version: {{ preisliste.version }}</span>
                        </div>
                      </div>

                      <div v-if="!preisliste && !loading" class="alert alert-warning">{{ $t('dm_kalk_no_preisliste') }}</div>

                      <div v-if="preisliste">
                        <!-- WTG Selection (shared between both modes) -->
                        <div class="card card-bordered mb-4">
                          <div class="card-inner">
                            <h6 class="overline-title overline-title-alt mb-4">{{ $t('dm_kalk_section_wtg') }}</h6>
                            <div class="row g-4">
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_hersteller') }}</label>
                                  <select class="form-control" v-model="selectedHersteller" @change="onHerstellerChange">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="h in herstellerList" :key="h" :value="h">{{ h }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_typ') }}</label>
                                  <select class="form-control" v-model="selectedTyp" @change="onTypChange" :disabled="!selectedHersteller">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="t in filteredTypen" :key="t.typ" :value="t.typ">{{ t.typ }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_nabenhoehen') }}</label>
                                  <select class="form-control" v-model.number="selectedNH" :disabled="!selectedTyp">
                                    <option value="0">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="nh in wtgNabenhoehen" :key="nh" :value="nh">{{ nh }} m</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_anzahl_wea') }}</label>
                                  <input type="number" min="1" class="form-control" v-model.number="anzahlWEA" />
                                </div>
                              </div>
                            </div>
                            <!-- Auto-populated WTG data -->
                            <div v-if="wtg" class="dm-wtg-badges mt-4 pt-3" style="border-top: 1px solid rgba(128, 128, 128, 0.15);">
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_durchmesser') }}: {{ wtg.durchmesser }} m</span>
                              <span class="badge badge-dim badge-outline-gray">kW: {{ wtg.kw }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_turmtyp') }}: {{ wtg.turmtyp || '—' }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_trafo') }}: {{ wtg.trafo_position || '—' }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_getriebe') }}: {{ wtg.getriebe }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_pruefzeit') }}: {{ wtg.pruefzeit }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Mode Tabs -->
                        <ul class="nav nav-tabs">
                          <li class="nav-item">
                            <a class="nav-link" :class="{ active: mode === 'standard' }" href="#" @click.prevent="mode = 'standard'">{{ $t('dm_kalk_tab_standard') }}</a>
                          </li>
                          <li class="nav-item">
                            <a class="nav-link" :class="{ active: mode === 'individuell' }" href="#" @click.prevent="mode = 'individuell'">{{ $t('dm_kalk_tab_individuell') }}</a>
                          </li>
                        </ul>

                        <div class="card card-bordered" style="border-top: none; border-top-left-radius: 0; border-top-right-radius: 0;">
                          <div class="card-inner">

                            <!-- STANDARD MODE -->
                            <div v-show="mode === 'standard'">
                              <!-- Hauptprüfung -->
                              <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_kalk_section_hauptpruefung') }}</h6>
                              <div class="row g-4 mb-4 pb-4" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15);">
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">{{ $t('dm_kalk_label_pruefungstyp') }}</label>
                                    <select class="form-control" v-model="selectedPruefTyp">
                                      <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                      <option value="ibn">IBN (Inbetriebnahme)</option>
                                      <option value="eow">EoW (End of Warranty)</option>
                                      <option value="wkp_basic">WKP-basic</option>
                                      <option value="wkp_plus">WKP-Plus</option>
                                      <option value="zop">ZOP</option>
                                    </select>
                                  </div>
                                </div>
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">&nbsp;</label>
                                    <div class="form-control-wrap dm-checkbox-align">
                                      <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="cbExclTrafo" v-model="exclTrafo" />
                                        <label class="custom-control-label" for="cbExclTrafo">{{ $t('dm_kalk_label_trafo_excl_hint') }}</label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">&nbsp;</label>
                                    <div class="form-control-wrap dm-checkbox-align">
                                      <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="cbBegleit" v-model="begleitpersonProvided" />
                                        <label class="custom-control-label" for="cbBegleit">{{ $t('dm_kalk_label_begleitperson_hint') }}</label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <!-- Zusatzprodukte -->
                              <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_kalk_section_zusatz') }}</h6>
                              <div class="row g-3 mb-4 pb-4" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15);">
                                <div class="col-md-6" v-for="zp in zusatzProdukte" :key="zp.key">
                                  <div class="dm-zusatz-row">
                                    <div class="custom-control custom-checkbox">
                                      <input type="checkbox" class="custom-control-input" :id="'zp_' + zp.key" v-model="zp.active" />
                                      <label class="custom-control-label" :for="'zp_' + zp.key">{{ zp.label }}</label>
                                    </div>
                                    <select v-if="zp.active && zusatzOptionen[zp.key] && zusatzOptionen[zp.key].length > 1" class="form-control form-control-sm ml-3" style="width: 220px;" v-model="zp.selected">
                                      <option v-for="opt in zusatzOptionen[zp.key]" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                                    </select>
                                  </div>
                                </div>
                              </div>

                              <!-- Included positions -->
                              <div v-if="wtg && (selectedPruefTyp || hasActiveZusatz)">
                                <h6 class="overline-title overline-title-alt mb-2">{{ $t('dm_kalk_section_positions') }}</h6>
                                <div v-for="(group, gIdx) in standardGroups" :key="gIdx" class="mb-3">
                                  <div class="dm-group-header-sm">
                                    <strong>{{ group.produkt }}</strong>
                                    <span class="float-right text-primary fw-bold">{{ formatPrice(group.subtotal) }} EUR</span>
                                  </div>
                                  <table class="dm-table dm-table-compact">
                                    <tbody>
                                      <tr v-for="(pos, pIdx) in group.items" :key="pIdx" :class="{ 'dm-row-excluded': pos.excluded }">
                                        <td style="width: 5%" v-if="pos.inclusion === 'ggf'">
                                          <div class="custom-control custom-checkbox">
                                            <input type="checkbox" class="custom-control-input" :id="'std_' + gIdx + '_' + pIdx" :checked="!pos.excluded" @change="toggleExcluded(pos._idx)" />
                                            <label class="custom-control-label" :for="'std_' + gIdx + '_' + pIdx"></label>
                                          </div>
                                        </td>
                                        <td style="width: 5%" v-else></td>
                                        <td style="width: 30%">{{ pos.modul }}</td>
                                        <td style="width: 30%" class="text-muted small">{{ pos.detail }}</td>
                                        <td style="width: 10%" class="text-center">
                                          <span class="badge" :class="pos.inclusion === 'x' ? 'badge-success' : 'badge-warning'">{{ pos.inclusion === 'ggf' ? 'optional' : pos.inclusion }}</span>
                                        </td>
                                        <td style="width: 20%" class="text-right fw-bold">
                                          <span v-if="pos.excluded" class="text-muted">—</span>
                                          <template v-else-if="pos.isAuftragsabh">
                                            <div class="d-flex align-items-center justify-content-end">
                                              <input type="number" step="0.01" min="0" class="form-control form-control-sm dm-manual-price" v-model.number="manualPrices[pos._idx]" placeholder="Preis" @input="onManualPriceInput" />
                                              <span class="ml-1 small text-muted">EUR</span>
                                            </div>
                                          </template>
                                          <span v-else-if="typeof pos.calculatedPrice === 'number'">{{ formatPrice(pos.calculatedPrice) }} EUR</span>
                                          <span v-else class="text-muted small">{{ pos.calculatedPrice }}</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            <!-- INDIVIDUELL MODE -->
                            <div v-show="mode === 'individuell'">
                              <div v-if="!wtg" class="alert alert-info">{{ $t('dm_kalk_select_wtg_first') }}</div>
                              <div v-if="wtg">
                                <div class="d-flex align-items-center mb-4 pb-3" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15); gap: 12px;">
                                  <label class="form-label mb-0 text-muted small text-nowrap">{{ $t('dm_kalk_label_vorlage') }}:</label>
                                  <select class="form-control form-control-sm" style="width: 200px;" v-model="indVorlage" @change="applyVorlage">
                                    <option value="">-- {{ $t('dm_kalk_label_keine') }} --</option>
                                    <option value="ibn">IBN</option>
                                    <option value="eow">EoW</option>
                                    <option value="wkp_basic">WKP-basic</option>
                                    <option value="wkp_plus">WKP-Plus</option>
                                    <option value="zop">ZOP</option>
                                  </select>
                                  <button class="btn btn-sm btn-outline-secondary" @click="clearIndividuell">{{ $t('dm_kalk_btn_reset') }}</button>
                                </div>
                                <div v-for="(group, gIdx) in individuellGroups" :key="gIdx" class="mb-4">
                                  <div class="dm-group-header-sm" @click="group.collapsed = !group.collapsed" style="cursor: pointer;">
                                    <em class="icon ni" :class="group.collapsed ? 'ni-chevron-right' : 'ni-chevron-down'"></em>
                                    <strong>{{ group.produkt }}</strong>
                                    <span class="float-right text-primary fw-bold">{{ formatPrice(group.subtotal) }} EUR</span>
                                  </div>
                                  <table v-show="!group.collapsed" class="dm-table dm-table-compact">
                                    <tbody>
                                      <tr v-for="(pos, pIdx) in group.items" :key="pIdx" :class="{ 'bg-light': pos.checked }">
                                        <td style="width: 5%">
                                          <div class="custom-control custom-checkbox">
                                            <input type="checkbox" class="custom-control-input" :id="'ind_' + gIdx + '_' + pIdx" v-model="pos.checked" />
                                            <label class="custom-control-label" :for="'ind_' + gIdx + '_' + pIdx"></label>
                                          </div>
                                        </td>
                                        <td style="width: 35%">{{ pos.modul }}</td>
                                        <td style="width: 35%" class="text-muted small">{{ pos.detail }}</td>
                                        <td style="width: 25%" class="text-right fw-bold">
                                          <template v-if="pos.checked && pos.isAuftragsabh">
                                            <div class="d-flex align-items-center justify-content-end">
                                              <input type="number" step="0.01" min="0" class="form-control form-control-sm dm-manual-price" v-model.number="pos.manualPrice" placeholder="Preis" />
                                              <span class="ml-1 small text-muted">EUR</span>
                                            </div>
                                          </template>
                                          <span v-else-if="pos.checked && typeof pos.calculatedPrice === 'number'">{{ formatPrice(pos.calculatedPrice) }} EUR</span>
                                          <span v-else class="text-muted">—</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                          </div>
                        </div>

                        <!-- Nebenkosten Section -->
                        <div class="card card-bordered mt-4" v-if="wtg">
                          <div class="card-inner">
                            <h6 class="overline-title overline-title-alt mb-4">{{ $t('dm_pl_tab_nebenkosten') }}</h6>
                            <div class="row g-4">
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_km_hin') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.km_hin" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_km_rueck') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.km_rueck" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_reisezeit_hin') }}</label>
                                  <input type="number" min="0" step="0.5" class="form-control" v-model.number="nk.reisezeit_hin" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_reisezeit_rueck') }}</label>
                                  <input type="number" min="0" step="0.5" class="form-control" v-model.number="nk.reisezeit_rueck" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_uebernachtungen') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.uebernachtungen" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_ganztage') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.ganztage" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_halbtage') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.halbtage" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_personen') }} / {{ $t('dm_kalk_label_autos') }}</label>
                                  <div class="d-flex" style="gap: 8px;">
                                    <input type="number" min="1" class="form-control" v-model.number="nk.personen" :placeholder="$t('dm_kalk_label_personen')" />
                                    <input type="number" min="1" class="form-control" v-model.number="nk.autos" :placeholder="$t('dm_kalk_label_autos')" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Total Summary -->
                        <div class="card card-bordered mt-4 dm-summary-card" v-if="wtg && (selectedPruefTyp || hasActiveZusatz || mode === 'individuell')">
                          <div class="card-inner">
                            <h6 class="overline-title overline-title-alt mb-4">{{ $t('dm_kalk_section_summary') }}</h6>
                            <table class="dm-summary-table">
                              <tbody>
                                <tr>
                                  <td>{{ $t('dm_kalk_label_pruefpreis') }}</td>
                                  <td class="text-right fw-bold">{{ formatPrice(pruefPreis) }} EUR</td>
                                </tr>
                                <tr v-if="anzahlWEA > 1">
                                  <td>x {{ anzahlWEA }} WEA</td>
                                  <td class="text-right fw-bold">{{ formatPrice(pruefPreis * anzahlWEA) }} EUR</td>
                                </tr>
                                <tr>
                                  <td>{{ $t('dm_kalk_label_rabatt') }} ({{ discountLabel }})</td>
                                  <td class="text-right">{{ formatPrice(discountAmount) }} EUR</td>
                                </tr>
                                <tr>
                                  <td>{{ $t('dm_pl_tab_nebenkosten') }}</td>
                                  <td class="text-right">{{ formatPrice(nebenkostenTotal) }} EUR</td>
                                </tr>
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td><strong>{{ $t('dm_kalk_label_gesamt') }}</strong></td>
                                  <td class="text-right"><strong class="dm-total-price">{{ formatPrice(gesamtPreis) }} EUR</strong></td>
                                </tr>
                                <tr>
                                  <td colspan="2" class="text-muted small pt-1">{{ $t('dm_kalk_label_gesamt_netto') }}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getPreislisteList, getPreisliste } from "@/services/dm.service";

export default {
  name: "DmKalkulator",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      preisliste: null,
      loading: false,
      mode: "standard",
      // WTG selection
      selectedHersteller: "",
      selectedTyp: "",
      selectedNH: 0,
      anzahlWEA: 1,
      // Standard mode
      selectedPruefTyp: "",
      exclTrafo: false,
      begleitpersonProvided: false,
      excludedPositions: {},  // key: berechnung index, value: true if excluded
      manualPrices: {},       // key: berechnung index, value: manually entered price for auftragsabh. positions
      manualPricesTick: 0,    // reactivity trigger
      zusatzProdukte: [
        { key: "swm", label: "Schwingungsmessung (SWM)", active: false, selected: "", produktMatch: "swm" },
        { key: "swa", label: "Schwingungsanalyse (SWA)", active: false, selected: "", produktMatch: "swa" },
        { key: "thermo", label: "Thermografie", active: false, selected: "", produktMatch: "thermografie" },
        { key: "ve", label: "Videoendoskopie (VE)", active: false, selected: "", produktMatch: "ve" },
        { key: "dguv", label: "DGUV V3", active: false, selected: "", produktMatch: "dguv" },
        { key: "rbwinkel", label: "Blattwinkelvermessung", active: false, selected: "", produktMatch: "rb-winkel" },
        { key: "vtb", label: "Vertikale Turmbewegung (VTB)", active: false, selected: "", produktMatch: "vtb" },
      ],
      // Individuell mode
      indVorlage: "",
      individuellItems: [],
      // Nebenkosten
      nk: {
        km_hin: 0, km_rueck: 0,
        reisezeit_hin: 0, reisezeit_rueck: 0,
        uebernachtungen: 0,
        ganztage: 0, halbtage: 0,
        personen: 1, autos: 1,
      },
    };
  },
  computed: {
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page === "setup";
    },
    herstellerList() {
      if (!this.preisliste || !this.preisliste.dropdowns) return [];
      return this.preisliste.dropdowns.hersteller || [];
    },
    filteredTypen() {
      if (!this.preisliste || !this.selectedHersteller) return [];
      return (this.preisliste.anlagendaten || []).filter(function(a) { return a.hersteller === this.selectedHersteller; }.bind(this));
    },
    wtg() {
      if (!this.selectedTyp || !this.preisliste) return null;
      var found = (this.preisliste.anlagendaten || []).find(function(a) { return a.typ === this.selectedTyp; }.bind(this));
      return found || null;
    },
    wtgNabenhoehen() {
      return this.wtg ? (this.wtg.nabenhoehen || []) : [];
    },
    // Build dropdown options per Zusatzprodukt from berechnung data
    zusatzOptionen() {
      if (!this.preisliste || !this.preisliste.berechnung) return {};
      var result = {};
      var berechnung = this.preisliste.berechnung;
      for (var z = 0; z < this.zusatzProdukte.length; z++) {
        var zp = this.zusatzProdukte[z];
        var match = zp.produktMatch.toLowerCase();
        var options = [];
        var seen = {};
        for (var i = 0; i < berechnung.length; i++) {
          var pos = berechnung[i];
          var produkt = (pos.produkt || "").toLowerCase().trim();
          // Match: "swm" matches "SWM ", "ve" matches "VE", "dguv" matches "DGUV V3", etc.
          var isMatch = false;
          if (match === "thermografie") isMatch = produkt.indexOf("thermografie") > -1;
          else if (match === "rb-winkel") isMatch = produkt.indexOf("rb-winkel") > -1 || produkt.indexOf("winkel") > -1;
          else isMatch = produkt.indexOf(match) > -1;
          if (isMatch) {
            var detail = (pos.detail || "").trim();
            var label = detail || (pos.modul || "").trim();
            if (!label || label === "[Platzhalter]") continue;
            var key = label.toLowerCase();
            if (seen[key]) continue;
            seen[key] = true;
            options.push({ value: "" + i, label: label });
          }
        }
        result[zp.key] = options;
      }
      return result;
    },
    stundensatz() {
      return (this.preisliste && this.preisliste.berechnungs_saetze) ? this.preisliste.berechnungs_saetze.stundensatz : 116;
    },
    tagessatz() {
      return (this.preisliste && this.preisliste.berechnungs_saetze) ? this.preisliste.berechnungs_saetze.tagessatz : 935;
    },
    nkSaetze() {
      return (this.preisliste && this.preisliste.nebenkosten_saetze) || {
        verpflegung_ganztag: 28, verpflegung_halbtag: 14,
        reisezeit_pro_h: 79, uebernachtung_pro_nacht: 120, km_pauschale: 0.49,
      };
    },
    hasActiveZusatz() {
      return this.zusatzProdukte.some(function(zp) { return zp.active; });
    },
    // Standard mode: filter berechnung by pruefungstyp + active Zusatzprodukte
    standardGroups() {
      if (!this.wtg || !this.preisliste) return [];
      if (!this.selectedPruefTyp && !this.hasActiveZusatz) return [];
      var self = this;
      var berechnung = this.preisliste.berechnung || [];
      var pt = this.selectedPruefTyp;
      var included = [];

      // Build set of active Zusatzprodukt matches
      var activeZusatz = {};
      for (var z = 0; z < self.zusatzProdukte.length; z++) {
        if (self.zusatzProdukte[z].active) {
          activeZusatz[self.zusatzProdukte[z].produktMatch] = self.zusatzProdukte[z];
        }
      }

      for (var i = 0; i < berechnung.length; i++) {
        var pos = berechnung[i];
        var modul = (pos.modul || "").toLowerCase();
        var inc = "";

        // 1. Hauptprüfung: match via pruefungstypen columns
        if (pt && pos.pruefungstypen) {
          var ptInc = pos.pruefungstypen[pt] || "";
          if (ptInc === "x" || ptInc === "ggf") {
            // Exclusion logic
            if (self.exclTrafo && modul.indexOf("trafo") > -1) continue;
            if (self.begleitpersonProvided && modul.indexOf("begleitperson") > -1) continue;
            inc = ptInc;
          }
        }

        // 2. Zusatzprodukte: match by berechnung index (selected value = index in berechnung array)
        if (!inc) {
          for (var zKey in activeZusatz) {
            var zp = activeZusatz[zKey];
            var opts = self.zusatzOptionen[zp.key] || [];
            if (opts.length <= 1) {
              // No sub-options (e.g. RB-Winkel, VTB) — include all matching positions
              for (var o = 0; o < opts.length; o++) {
                if (parseInt(opts[o].value) === i) { inc = "x"; break; }
              }
            } else {
              // Has sub-options — only include the selected one
              if (zp.selected && parseInt(zp.selected) === i) inc = "x";
            }
          }
        }

        if (inc) {
          var price = self.calcPrice(pos);
          var isAuftr = price === "auftragsabh.";
          if (isAuftr && self.manualPrices[i] > 0 && self.manualPricesTick >= 0) {
            price = self.manualPrices[i];
          }
          included.push({
            produkt: pos.produkt,
            modul: pos.modul,
            detail: pos.detail,
            inclusion: inc,
            _idx: i,
            excluded: !!self.excludedPositions[i],
            isAuftragsabh: isAuftr,
            calculatedPrice: price,
          });
        }
      }
      return self.groupByProdukt(included);
    },
    // Individuell mode: all positions with checkboxes
    individuellGroups() {
      if (!this.wtg) return [];
      var self = this;
      var groups = [];
      var map = {};
      for (var i = 0; i < this.individuellItems.length; i++) {
        var item = this.individuellItems[i];
        var cp = self.calcPrice(item._src);
        // For auftragsabh: use manual price if entered
        if (cp === "auftragsabh." && item.manualPrice > 0) {
          cp = item.manualPrice;
        }
        item.calculatedPrice = cp;
        var key = item.produkt;
        if (!map[key]) {
          map[key] = { produkt: key, collapsed: false, items: [], subtotal: 0 };
          groups.push(map[key]);
        }
        map[key].items.push(item);
        if (item.checked && typeof item.calculatedPrice === "number") {
          map[key].subtotal += item.calculatedPrice;
        }
      }
      return groups;
    },
    // Price calculations
    pruefPreis() {
      if (this.mode === "standard") {
        var total = 0;
        for (var g = 0; g < this.standardGroups.length; g++) {
          total += this.standardGroups[g].subtotal;
        }
        return total;
      } else {
        var total2 = 0;
        for (var g2 = 0; g2 < this.individuellGroups.length; g2++) {
          total2 += this.individuellGroups[g2].subtotal;
        }
        return total2;
      }
    },
    discountFactor() {
      var n = this.anzahlWEA;
      if (n <= 1) return 1.05;
      if (n <= 3) return 1.0;
      if (n <= 7) return 0.95;
      return 0.90;
    },
    discountLabel() {
      var f = this.discountFactor;
      if (f > 1) return "+" + Math.round((f - 1) * 100) + "% (1 WEA)";
      if (f === 1) return "0%";
      return "-" + Math.round((1 - f) * 100) + "%";
    },
    discountAmount() {
      var base = this.pruefPreis * this.anzahlWEA;
      return base * this.discountFactor - base;
    },
    nebenkostenTotal() {
      var s = this.nkSaetze;
      var nk = this.nk;
      var verpflegung = (nk.ganztage * s.verpflegung_ganztag + nk.halbtage * s.verpflegung_halbtag) * nk.personen;
      var reisezeit = s.reisezeit_pro_h * (nk.reisezeit_hin + nk.reisezeit_rueck) * nk.autos;
      var uebernachtung = s.uebernachtung_pro_nacht * nk.uebernachtungen * nk.personen;
      var km = s.km_pauschale * (nk.km_hin + nk.km_rueck) * nk.autos;
      return verpflegung + reisezeit + uebernachtung + km;
    },
    gesamtPreis() {
      var base = this.pruefPreis * this.anzahlWEA;
      return base * this.discountFactor + this.nebenkostenTotal;
    },
  },
  watch: {
    "preisliste.berechnung": function() { this.buildIndividuellItems(); },
  },
  created() {
    this.loadPreisliste();
  },
  methods: {
    loadPreisliste() {
      var self = this;
      self.loading = true;
      self.$store.commit("set_loading_page", "kalkulator");
      getPreislisteList()
        .then(function(d) {
          var list = (d.data && d.data.list) || [];
          if (list.length > 0) {
            // Load the first (= active) preisliste
            return getPreisliste(list[0]._id);
          }
          self.$store.commit("set_loading_page", null);
          self.loading = false;
        })
        .then(function(d) {
          self.$store.commit("set_loading_page", null);
          self.loading = false;
          if (d && d.data && d.data.preisliste) {
            self.preisliste = d.data.preisliste;
            self.buildIndividuellItems();
          }
        })
        .catch(function(e) {
          self.$store.commit("set_loading_page", null);
          self.loading = false;
          self.$store.dispatch("api_error", e);
        });
    },
    loadPreislisteDetail(id) {
      var self = this;
      self.$store.commit("set_loading_page", "kalkulator");
      getPreisliste(id)
        .then(function(d) {
          self.$store.commit("set_loading_page", null);
          if (d && d.data && d.data.preisliste) {
            self.preisliste = d.data.preisliste;
            self.buildIndividuellItems();
          }
        })
        .catch(function(e) {
          self.$store.commit("set_loading_page", null);
          self.$store.dispatch("api_error", e);
        });
    },
    onHerstellerChange() {
      this.selectedTyp = "";
      this.selectedNH = 0;
    },
    onTypChange() {
      this.selectedNH = 0;
      // Auto-select first Nabenhoehe if only one
      if (this.wtg && this.wtg.nabenhoehen && this.wtg.nabenhoehen.length === 1) {
        this.selectedNH = this.wtg.nabenhoehen[0];
      }
    },
    buildIndividuellItems() {
      if (!this.preisliste || !this.preisliste.berechnung) return;
      this.individuellItems = this.preisliste.berechnung.map(function(pos) {
        var isAuftr = (pos.formel_typ === "auftragsabhaengig") || (pos.preis_typ === "auftragsabhaengig");
        return {
          produkt: pos.produkt,
          modul: pos.modul,
          detail: pos.detail,
          checked: false,
          calculatedPrice: 0,
          manualPrice: 0,
          isAuftragsabh: isAuftr,
          _src: pos,
        };
      });
      // Auto-select first option for each Zusatzprodukt
      for (var z = 0; z < this.zusatzProdukte.length; z++) {
        var zp = this.zusatzProdukte[z];
        var opts = this.zusatzOptionen[zp.key] || [];
        if (opts.length > 0 && !zp.selected) {
          zp.selected = opts[0].value;
        }
      }
    },
    applyVorlage: function() {
      var pt = this.indVorlage;
      if (!pt || !this.individuellItems.length) return;
      for (var i = 0; i < this.individuellItems.length; i++) {
        var item = this.individuellItems[i];
        var src = item._src;
        var inc = src.pruefungstypen ? (src.pruefungstypen[pt] || "") : "";
        item.checked = (inc === "x" || inc === "ggf");
      }
    },
    clearIndividuell: function() {
      this.indVorlage = "";
      for (var i = 0; i < this.individuellItems.length; i++) {
        this.individuellItems[i].checked = false;
        this.individuellItems[i].manualPrice = 0;
      }
    },
    onManualPriceInput: function() {
      this.manualPricesTick++;
    },
    toggleExcluded: function(idx) {
      this.$set(this.excludedPositions, idx, !this.excludedPositions[idx]);
    },
    groupByProdukt: function(items) {
      var groups = [];
      var map = {};
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var key = item.produkt;
        if (!map[key]) {
          map[key] = { produkt: key, items: [], subtotal: 0 };
          groups.push(map[key]);
        }
        map[key].items.push(item);
        if (!item.excluded && typeof item.calculatedPrice === "number") {
          map[key].subtotal += item.calculatedPrice;
        }
      }
      return groups;
    },
    /**
     * Data-driven price calculation engine.
     * Reads formel_typ, formel_faktor, formel_basis from each position.
     * Falls back to legacy hardcoded logic if formel_typ is not set.
     */
    calcPrice: function(pos) {
      if (!pos || !this.wtg) return 0;
      var w = this.wtg;
      var nh = this.selectedNH || (w.nabenhoehen && w.nabenhoehen[0]) || 0;
      var rd = w.durchmesser || 0;
      var kw = w.kw || 0;
      var satz = this.stundensatz;
      var typ = pos.formel_typ || "";
      var faktor = pos.formel_faktor || 0;
      var basis = pos.formel_basis || 0;

      // Data-driven calculation
      switch (typ) {
        case "festpreis":
          return basis || pos.preis || 0;
        case "stundensatz_faktor":
          return Math.round(faktor * satz * 100) / 100;
        case "nh_faktor":
          return Math.round(faktor * nh * 100) / 100;
        case "rd_faktor":
          return Math.round(faktor * rd * 100) / 100;
        case "basis_plus_rd":
          return Math.round((basis + faktor * rd) * 100) / 100;
        case "basis_plus_nh":
          return Math.round((basis + faktor * nh) * 100) / 100;
        case "pruefzeit_stundensatz":
          return Math.round(w.pruefzeit * satz * (faktor || 1) * 100) / 100;
        case "anlagendaten":
          // Resolve field from detail text
          var det = (pos.detail || pos.modul || "").toLowerCase();
          if (det.indexOf("innen") > -1) return w.festpreis_rb_innen || 0;
          if (det.indexOf("au") > -1 && det.indexOf("rotorbl") > -1) return w.festpreis_rb_aussen || 0;
          return w.blitzschutz_pauschale || 0;
        case "kw_staffel":
          // basis = Grundpreis, faktor = Zuschlag-Schwelle (vereinfacht)
          var p = basis;
          if (kw > 2500) p += faktor;
          if (kw > 3500) p += faktor;
          if (kw > 5000) p += faktor;
          return p;
        case "einzelpreis_staffel":
          var ep = (this.preisliste.einzelpreise || [])[0];
          if (ep && ep.staffeln) {
            var staffel = ep.staffeln.find(function(s) { return s.anzahl === this.anzahlWEA; }.bind(this));
            if (!staffel) staffel = ep.staffeln[ep.staffeln.length - 1];
            if (staffel) {
              var sp = staffel.basispreis;
              if (nh >= 130) sp += staffel.zuschlaege.nh_ab_130;
              else if (nh >= 100) sp += staffel.zuschlaege.nh_ab_100;
              if (rd >= 150) sp += staffel.zuschlaege.rd_ab_150;
              else if (rd >= 130) sp += staffel.zuschlaege.rd_ab_130;
              else if (rd >= 100) sp += staffel.zuschlaege.rd_ab_100;
              return sp;
            }
          }
          return basis || 0;
        case "auftragsabhaengig":
          return "auftragsabh.";
        default:
          // Legacy fallback: use stored preis if set
          if (pos.preis > 0) return pos.preis;
          return 0;
      }
    },
    formatPrice: function(val) {
      if (val == null || val === "" || typeof val !== "number") return "—";
      return val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.dm-group-header-sm {
  padding: 10px 14px;
  background: rgba(128, 128, 128, 0.08);
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 14px;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.dm-table-compact td {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.1);
}

.dm-table tbody tr:hover {
  background: rgba(128, 128, 128, 0.06);
}

.nav-tabs .nav-link {
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
}

.nav-tabs .nav-link.active {
  color: #E8751A;
}

.text-right {
  text-align: right;
}

.fw-bold {
  font-weight: 600;
}

.bg-light {
  background: rgba(59, 130, 246, 0.08) !important;
}

.dm-wtg-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dm-wtg-badges .badge {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
}

.dm-summary-card {
  max-width: 560px;
}

.dm-summary-table {
  width: 100%;
  border-collapse: collapse;
}

.dm-summary-table td {
  padding: 8px 0;
  font-size: 14px;
}

.dm-summary-table tfoot tr:first-child td {
  padding-top: 12px;
  border-top: 2px solid rgba(128, 128, 128, 0.2);
}

.dm-total-price {
  color: #E8751A;
  font-size: 20px;
}

.form-group {
  margin-bottom: 0;
}

.dm-checkbox-align {
  height: calc(2.125rem + 2px);
  display: flex;
  align-items: center;
}

.dm-row-excluded td {
  opacity: 0.4;
  text-decoration: line-through;
}

.dm-manual-price {
  width: 100px;
  text-align: right;
}

.dm-zusatz-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  background: rgba(128, 128, 128, 0.05);
  min-height: 44px;
}
</style>
